"use client";

import { usePathname } from "next/navigation";
import Script from "next/script";
import { useEffect, useState } from "react";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { apiFetch } from "@/lib/api";
import { SessionResponse } from "@/types/chat";

// Routes where the floating support launcher gets in the way: immersive
// builders, and the full-page GailGPT chat it duplicates.
const HIDDEN_ROUTES = [/^\/workflows\/[^/]+\/builder/, /^\/skills/, /^\/gailgpt(\/|$)/];

const isSupportWidgetConfigured = (): boolean =>
  Boolean(
    process.env.NEXT_PUBLIC_SUPPORT_WIDGET_ORG_ID?.trim() &&
    process.env.NEXT_PUBLIC_SUPPORT_WIDGET_ASSISTANT_ID?.trim()
  );

declare global {
  namespace React.JSX {
    interface IntrinsicElements {
      "gail-widget": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          "org-id"?: string;
          "assistant-id"?: string;
          position?: string;
          environment?: string;
          "user-session"?: string;
          "user-reference"?: string;
          "base-href"?: string;
          verbose?: string;
        },
        HTMLElement
      >;
    }
  }
}

const SupportWidget = () => {
  const pathname = usePathname();
  const { user, isAuthenticated } = useAuthContext();

  const [sessionJwt, setSessionJwt] = useState<string | null>(null);

  const getSessionJwt = async () => {
    if (sessionJwt) {
      return;
    }

    if (!isSupportWidgetConfigured() || !user?.orgId || !user?.agencyId) {
      setSessionJwt(null);
      return;
    }

    const response = await apiFetch<SessionResponse>(`/api/session`, "POST");

    if (response) {
      setSessionJwt(response.id);
    }
  };

  useEffect(() => {
    getSessionJwt();
  }, [user?.id, user?.orgId]);

  if (
    !isAuthenticated ||
    !user ||
    !sessionJwt ||
    !isSupportWidgetConfigured() ||
    HIDDEN_ROUTES.some((route) => route.test(pathname))
  ) {
    return null;
  }

  return (
    <>
      <Script
        src="https://unpkg.com/@gail-ai/conversation-embed"
        type="module"
        crossOrigin="anonymous"
      />
      <gail-widget
        org-id={process.env.NEXT_PUBLIC_SUPPORT_WIDGET_ORG_ID}
        assistant-id={process.env.NEXT_PUBLIC_SUPPORT_WIDGET_ASSISTANT_ID}
        position="bottom-right"
        environment={process.env.NEXT_PUBLIC_ENVIRONMENT}
        user-session={sessionJwt ?? undefined}
        user-reference={user.orgId ?? undefined}
        base-href={process.env.NEXT_PUBLIC_BASE_URL}
      ></gail-widget>
    </>
  );
};

export default SupportWidget;
